import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ModuleProperties, AppContext } from '@/lib/core/types';
import {
  HomePageUpcomingBookingsElementModel,
  ImageWithCrops,
  ReservationSummaryDto,
} from '@/services/platform/api/model';
import {
  LinksToLinkMap,
  formatDate,
  formatTimeFromDate,
  ImageMap,
  ImagesToImageMap,
  useClientTranslation,
  brandifyJncquoi,
} from '@/lib/core';
import { useApp } from '@/lib/core/providers/appProvider';
import { useLocaleStore } from '@/lib/core/stores';
import { getUpcomingReservations } from '@/services/platform/data/booking';
import { eventTitle } from '@/services/platform/data/booking/bookingTitle';
import { cn } from '@/lib/core/utils/cn';
import { BrandCase } from '@/components/shared/brandCase';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import ModuleWrapper from '@/components/shared/module/wrapper';

// Gradient palettes mapped to venue categories
const CATEGORY_GRADIENTS: Record<string, string> = {
  'Eat & Drink': 'from-[#3d1a00] to-[#1a1a1a]',
  Restaurant: 'from-[#2a1500] to-[#1a1a1a]',
  Hotel: 'from-[#001a2a] to-[#1a1a1a]',
  Spa: 'from-[#001a14] to-[#1a1a1a]',
  default: 'from-[#1a1a2a] to-[#1a1a1a]',
};

function categoryGradient(category?: string | null) {
  return CATEGORY_GRADIENTS[category ?? ''] ?? CATEGORY_GRADIENTS.default;
}

// ── BookingCard ───────────────────────────────────────────────────────────────

interface BookingCardProps {
  booking: ReservationSummaryDto;
  context?: AppContext;
}

function BookingCard({ booking, context }: BookingCardProps) {
  const t = useClientTranslation();
  const { appSettings } = useApp();
  // An event booking is titled by the event, not by the room it happens in — `venue.name` here
  // would read "JNCQUOI CLUB" on every event card and lose which event was booked.
  const title = eventTitle(booking);
  const image =
    booking.serviceType === 'Event'
      ? booking.imageUrl
        ? { src: booking.imageUrl, alt: title ?? booking.venue?.name ?? '' }
        : ImagesToImageMap(appSettings?.properties?.eventNoPicImage)
      : booking.serviceType == 'Driver'
        ? ImageMap(booking?.serviceCategory?.image as ImageWithCrops | undefined)
        : ImageMap(booking.venue?.image as ImageWithCrops | undefined);
  const baseUrl = context?.settings?.properties?.bookingDetail?.route?.path;
  const bookingUrl = baseUrl ? `${baseUrl}/${booking.publicGuid}` : null;
  const bookingStatus = booking.status?.toLowerCase() ?? '';

  const card = (
    <article
      className={cn(
        'rounded-jncquoi relative flex h-40.5 w-85 shrink-0 flex-col overflow-hidden p-4',
        'bg-linear-to-b',
        categoryGradient(booking.serviceCategory?.name)
      )}
    >
      {image?.src && (
        <img
          src={image.src}
          alt={image.alt ?? booking.venue?.name ?? 'Booking image'}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      )}

      {/* Gradient depth overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 42%, rgba(0,0,0,0.8) 80%)' }}
      />

      {bookingStatus === 'pending' && (
        <>
          <div aria-hidden="true" className="absolute inset-0 bg-[#1A1A1A99]" />
          <span className="font-inter absolute top-2.5 left-2.5 inline-flex items-center bg-black/80 px-3 py-1.5 text-[10px] font-medium tracking-[0.5px] text-white uppercase backdrop-blur-[5px]">
            {t('App.Labels.PendingConfirmation')}
          </span>
        </>
      )}

      {/* Content pinned to bottom */}
      {/* Figma spec measures 8px, but that's the trimmed-cap-height gap; the browser's line-height
          box adds space the Figma trim doesn't count, so 10px is what visually matches. */}
      <div className="relative mt-auto flex w-full flex-col gap-2.5 leading-none text-white">
        <p className="font-pp-neue-montreal text-[16px] font-medium">
          {title ?? booking.venue?.name ?? booking.serviceCategory?.name ?? '—'}
        </p>
        <div className="flex w-full items-center justify-between">
          <span className="font-pp-neue-montreal text-[13px] font-light">
            {formatDate(booking.startDate)}
            {formatTimeFromDate(booking.startDate) ? `, ${formatTimeFromDate(booking.startDate)}` : ''}
          </span>
          {booking.serviceCategory && (
            <span className="font-didot text-[12px] italic">{booking.serviceCategory?.name}</span>
          )}
        </div>
      </div>
    </article>
  );

  return bookingUrl ? <Link to={bookingUrl}>{card}</Link> : card;
}

// ── HomePageUpcomingBookings ──────────────────────────────────────────────────

export const HomePageUpcomingBookings = (props: ModuleProperties) => {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);

  const context = props.context as AppContext | undefined;
  const content = props.content as HomePageUpcomingBookingsElementModel | undefined;
  const properties = content?.properties;
  const title = properties?.title ?? t('App.Labels.UpcomingBookingsTitle');
  const link = LinksToLinkMap(properties?.link ?? undefined);
  const viewAllUrl = link?.href ?? `/${locale.split('-')[0]}/upcoming-bookings`;
  const viewAllLabel = link?.label ?? t('App.Labels.ViewAll');

  const bookIntroUrl =
    context?.settings?.properties?.bookingIntro?.route?.path ?? `/${locale.split('-')[0]}/book-intro`;
  const [reservations, setReservations] = useState<ReservationSummaryDto[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getUpcomingReservations({ take: 7 }, locale)
      .then(data => setReservations(data ?? []))
      .catch(() => setReservations([]))
      .finally(() => setLoaded(true));
  }, [locale]);

  const displayedReservations = reservations.slice(0, 6);

  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onScroll = () => {
    if (!scrollRef.current) return;
    const cardWidth = 340 + 8;
    setActiveIndex(Math.round(scrollRef.current.scrollLeft / cardWidth));
  };

  if (!loaded) return null;

  return (
    <ModuleWrapper
      component={props}
      isFullWidth={true}
      classNames={{
        base: 'module__upcoming-bookings',
        wrapper: 'py-0 lg:py-0',
        grid: 'grid-cols-1 lg:grid-cols-1 gap-0',
      }}
    >
      <div className="col-span-1 flex flex-col gap-6 px-4 py-6">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <h2 className="font-pp-neue-montreal text-[24px] font-semibold tracking-normal text-white">
            {title}
          </h2>
          {reservations.length > 6 && (
            <Button asChild variant="tertiary-link" palette="white" size="sm">
              <Link to={viewAllUrl}>{viewAllLabel}</Link>
            </Button>
          )}
        </div>

        {reservations.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center gap-6 rounded-sm border border-none! border-zinc-500 bg-zinc-900 px-4 py-6">
            <div className="flex flex-col items-center">
              <div className="flex size-11 items-center justify-center rounded-full">
                <Icon
                  name="clock-rotate-right"
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="text-white"
                />
              </div>
              <p className="font-pp-neue-montreal text-center text-xs font-normal tracking-wide text-white uppercase">
                <BrandCase>{t('App.Labels.NoUpcomingBookingsYet')}</BrandCase>
              </p>
            </div>
            <Button asChild variant="primary-solid" palette="white" size="lg" className="w-full normal-case">
              <Link to={bookIntroUrl}>{brandifyJncquoi(t('App.Labels.BookJNcQUOI')?.toUpperCase())}</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Horizontal carousel — capped at 6 cards; overflow is reached via "View all" */}
            <div
              ref={scrollRef}
              onScroll={onScroll}
              className="flex w-[calc(100%+16px)] snap-x snap-mandatory scrollbar-none gap-2 overflow-x-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {displayedReservations.map(r => (
                <div key={r.publicGuid} className="snap-start">
                  <BookingCard booking={r} context={context} />
                </div>
              ))}
              <div className="w-4 shrink-0" aria-hidden="true" />
            </div>

            {/* Dot pagination */}
            {displayedReservations.length > 1 && (
              <div className="flex items-center justify-center gap-2" aria-hidden="true">
                {displayedReservations.map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'block h-px transition-all duration-300',
                      i === activeIndex ? 'w-4 bg-white' : 'w-4 bg-white/30'
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ModuleWrapper>
  );
};
